import { CalendarRange, MoreHorizontal, Pencil, Repeat2, Trash2 } from 'lucide-react';
import { useState, type FocusEvent } from 'react';

import { useI18n } from '../../i18n/useI18n';
import type { Category, Wallet } from '../../lib/api-types';
import { DeleteMovementDialog } from './DeleteMovementDialog';
import { formatMoney } from './dashboard-format';
import { LimitMovementDialog } from './LimitMovementDialog';
import { MovementEditorDialog } from './MovementEditorDialog';
import { OccurrenceEditorDialog } from './OccurrenceEditorDialog';
import type { PlanListItem } from './plan-data';

interface MovementCardProps {
  readonly categories: readonly Category[];
  readonly item: PlanListItem;
  readonly wallets: readonly Wallet[];
}

type MovementAction = 'delete' | 'edit' | 'limit' | null;

const frequencyLabels = {
  biweekly: 'dashboard.frequencyBiweekly',
  monthly: 'dashboard.frequencyMonthly',
  'one-time': 'dashboard.frequencyOneTime',
  weekly: 'dashboard.frequencyWeekly',
} as const;

const typeLabels = {
  expense: 'dashboard.expense',
  income: 'dashboard.income',
  transfer: 'dashboard.transfer',
} as const;

export function MovementCard({ categories, item, wallets }: MovementCardProps) {
  const { locale, t } = useI18n();
  const { transaction } = item;
  const [menuOpen, setMenuOpen] = useState(false);
  const [action, setAction] = useState<MovementAction>(null);
  const category =
    categories.find((categoryItem) => categoryItem.id === transaction.categoryId)?.name ??
    t('dashboard.unknownCategory');
  const wallet = wallets.find((walletItem) => walletItem.id === item.walletId)?.name ?? 'Wallet';
  const destinationWallet = wallets.find(
    (walletItem) => walletItem.id === transaction.toWalletId,
  )?.name;
  const dateParts = formatDateParts(item.date, locale);
  const note = item.note.trim() || t('dashboard.addNote');
  const isOccurrence = item.mode === 'occurrence';

  const chooseAction = (nextAction: Exclude<MovementAction, null>) => {
    setMenuOpen(false);
    setAction(nextAction);
  };

  const closeMenuOnBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) setMenuOpen(false);
  };

  return (
    <>
      <article
        className={`movement-card movement-card-${transaction.type}${
          transaction.active ? '' : ' is-inactive'
        }${isOccurrence ? ' is-occurrence' : ''}`}
      >
        <time className="movement-date" dateTime={item.date}>
          <strong>{dateParts.day}</strong>
          <span>{dateParts.month}</span>
          <small>{dateParts.year}</small>
        </time>

        <div className="movement-details">
          <div className="movement-meta-row">
            <span className="movement-frequency">
              {(isOccurrence || transaction.frequency !== 'one-time') && (
                <Repeat2 aria-hidden="true" />
              )}
              {isOccurrence ? t('plan.occurrence') : t(frequencyLabels[transaction.frequency])}
              {item.isAdjusted && <i>{t('plan.adjustedOccurrence')}</i>}
            </span>
            <span className="movement-wallet">
              {destinationWallet ? `${wallet} → ${destinationWallet}` : wallet}
            </span>
            <span className="movement-category">{category}</span>
            <div className="movement-menu-anchor" onBlur={closeMenuOnBlur}>
              <button
                aria-expanded={menuOpen}
                aria-haspopup="menu"
                aria-label={t('plan.options')}
                className="note-action"
                type="button"
                onClick={() => setMenuOpen((current) => !current)}
              >
                <MoreHorizontal aria-hidden="true" />
              </button>
              {menuOpen && (
                <div className="movement-action-menu" role="menu">
                  <button role="menuitem" type="button" onClick={() => chooseAction('edit')}>
                    <Pencil aria-hidden="true" />
                    {t(isOccurrence ? 'plan.editOccurrence' : 'plan.editSeries')}
                  </button>
                  {transaction.frequency !== 'one-time' && (
                    <button role="menuitem" type="button" onClick={() => chooseAction('limit')}>
                      <CalendarRange aria-hidden="true" /> {t('plan.limit')}
                    </button>
                  )}
                  <button
                    className="is-danger"
                    role="menuitem"
                    type="button"
                    onClick={() => chooseAction('delete')}
                  >
                    <Trash2 aria-hidden="true" />
                    {t(isOccurrence ? 'plan.deleteOccurrence' : 'plan.delete')}
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="movement-value-row">
            <strong className={`movement-value movement-${transaction.type}`}>
              {transaction.type === 'income' ? '+' : transaction.type === 'expense' ? '−' : ''}
              {formatMoney(item.amount, locale)}
            </strong>
            <span className="movement-type-pill">{t(typeLabels[transaction.type])}</span>
            <span className={`movement-note${item.note.trim() ? '' : ' is-placeholder'}`}>
              {note}
            </span>
          </div>
        </div>
      </article>

      {action === 'edit' &&
        (isOccurrence ? (
          <OccurrenceEditorDialog
            occurrenceDate={item.date}
            transaction={transaction}
            onClose={() => setAction(null)}
          />
        ) : (
          <MovementEditorDialog
            categories={categories}
            transaction={transaction}
            wallets={wallets}
            onClose={() => setAction(null)}
          />
        ))}
      {action === 'limit' && (
        <LimitMovementDialog transaction={transaction} onClose={() => setAction(null)} />
      )}
      {action === 'delete' && (
        <DeleteMovementDialog
          {...(isOccurrence ? { occurrenceDate: item.date } : {})}
          transaction={transaction}
          onClose={() => setAction(null)}
        />
      )}
    </>
  );
}

function formatDateParts(date: string, locale: string) {
  const value = new Date(`${date.slice(0, 10)}T00:00:00.000Z`);
  return {
    day: new Intl.DateTimeFormat(locale, { day: 'numeric', timeZone: 'UTC' }).format(value),
    month: new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' })
      .format(value)
      .replace('.', '')
      .toUpperCase(),
    year: new Intl.DateTimeFormat(locale, { year: 'numeric', timeZone: 'UTC' }).format(value),
  };
}
